import type { ParsedPaymentTerms } from "@/types/backtest";

/**
 * Parses early payment discount terms from common string formats:
 *   "2/10 Net 30"      → 2% off if paid within 10 days, net 30
 *   "1.5/15 Net 45"    → 1.5% / 15 days / net 45
 *   "2/10 N30"         → same as 2/10 Net 30
 *   "2/10 N/30"
 *   "2% 10 Days Net 30"
 *   "2-10 Net 30"
 *
 * Returns null when no discount terms are present (e.g. "Net 30", "Due on Receipt").
 */
export function parsePaymentTerms(raw: string | null | undefined): ParsedPaymentTerms | null {
  if (!raw) return null;

  const s = raw.trim().toUpperCase();

  // Primary format: "2/10 NET 30", "2/10 N30", "2/10 N/30", "2-10 NET 30"
  const primary = s.match(/^(\d+(?:\.\d+)?)\s*[/-]\s*(\d+)\s+(?:NET|N(?:ET)?)\s*\/?(\d+)/);
  if (primary) {
    return {
      discountPct: parseFloat(primary[1]),
      discountDays: parseInt(primary[2], 10),
      netDays: parseInt(primary[3], 10),
      raw,
    };
  }

  // "2% 10 DAYS NET 30" or "2% 10 NET 30"
  const pctFirst = s.match(/^(\d+(?:\.\d+)?)\s*%\s*(\d+)\s*(?:DAYS?)?\s*(?:NET|N)\s*(\d+)/);
  if (pctFirst) {
    return {
      discountPct: parseFloat(pctFirst[1]),
      discountDays: parseInt(pctFirst[2], 10),
      netDays: parseInt(pctFirst[3], 10),
      raw,
    };
  }

  return null;
}

export function formatPaymentTerms(terms: ParsedPaymentTerms): string {
  return `${terms.discountPct}/${terms.discountDays} Net ${terms.netDays}`;
}
